import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { previewSafeUrl } from '../../../library/previewHostImages';
import { containerHasBodyContent } from './configHelpers';

/**
 * Opmaak › Landingspagina: the active landing-page blocks (Pagina's ›
 * Landingspagina, in HOME_SECTIONS_ORDER order) drawn as a rough mockup
 * inside the shared page chrome (language bar / header / menu / footer).
 *
 * Deliberately NOT a replica of the storefront -- the point is to see at a
 * glance what ends up on the landing page and in what order: the carousel
 * with its first slide, the welcome text, the image grid with its columns and
 * cell sizes, the product rows/containers with their title and display style.
 * Each block carries a cog that opens the block's existing settings dialog.
 */

const IMG_BG = '#eeeeee';

// the first language that has content, so a shop that only fills FR still shows
const pickLang = (byLang, lang) => {
    if (!byLang || typeof byLang !== 'object') return [];
    for (const l of [lang, 'nl', 'en', 'fr']) {
        const v = byLang[l];
        if (Array.isArray(v) ? v.length : v) return v;
    }
    return byLang[lang] || [];
};

const imgUrl = (img) => {
    if (!img) return '';
    if (typeof img === 'string') return img;
    return img.url || img.dataUrl || '';
};

const stripHtml = (html) => String(html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

const Placeholder = ({ label, height = '100%' }) => (
    <Box sx={{ width: '100%', height, backgroundColor: IMG_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9a9a9a', fontSize: '0.7rem', letterSpacing: 0.5 }}>
        {label}
    </Box>
);
Placeholder.propTypes = { label: PropTypes.node, height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

const ImageBox = ({ url, fit = 'cover', label, sx = {} }) => (
    url
        ? <Box sx={{ width: '100%', height: '100%', backgroundImage: `url("${previewSafeUrl(url)}")`, backgroundSize: fit, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundColor: IMG_BG, ...sx }} />
        : <Placeholder label={label} />
);
ImageBox.propTypes = { url: PropTypes.string, fit: PropTypes.string, label: PropTypes.node, sx: PropTypes.object };

// one product tile, same simple language as the Verkenner mockup
const ProductTile = ({ i, colors }) => (
    <Box sx={{ minWidth: 0 }}>
        <Box sx={{ position: 'relative', aspectRatio: '1 / 1', mb: 0.5 }}>
            <Placeholder label="IMG" />
        </Box>
        <Typography noWrap sx={{ fontSize: '0.68rem', fontWeight: 600, color: colors.title }}>Product {i}</Typography>
        <Typography noWrap sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.primary }}>
            € {(99 + i * 10).toFixed(2)}
        </Typography>
    </Box>
);
ProductTile.propTypes = { i: PropTypes.number, colors: PropTypes.object };

// a product row in one of the storefront's display styles
const ProductRow = ({ title, titleAlign = 'left', view = 'horizontal', count = 4, columns = 4, colors, button }) => {
    // six per row keeps a list in proportion with the (scaled-down) carousel
    // and grid; a grid container uses its own products-per-row and shows two
    // rows when it holds more
    const perRow = view === 'grid' ? Math.min(5, Math.max(1, Number(columns) || 4)) : 6;
    const shown = view === 'grid' && count > perRow ? perRow * 2 : perRow;
    return (
        <Box>
            {title && (
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: colors.title, textAlign: titleAlign, mb: 1 }}>{title}</Typography>
            )}
            <Box sx={{ position: 'relative', px: view === 'horizontal' ? 2.5 : 0 }}>
                {view === 'horizontal' && (
                    <>
                        <Box sx={{ position: 'absolute', left: 0, top: '38%', fontSize: '1rem', color: '#777' }}>‹</Box>
                        <Box sx={{ position: 'absolute', right: 0, top: '38%', fontSize: '1rem', color: '#777' }}>›</Box>
                    </>
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))`, gap: 1.25 }}>
                    {Array.from({ length: shown }, (_, k) => <ProductTile key={k} i={k + 1} colors={colors} />)}
                </Box>
            </Box>
            {button && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.25 }}>
                    <Box sx={{ px: 1.5, py: 0.5, borderRadius: 0.75, backgroundColor: colors.primary, color: '#fff', fontSize: '0.68rem', fontWeight: 600 }}>{button}</Box>
                </Box>
            )}
        </Box>
    );
};
ProductRow.propTypes = {
    title: PropTypes.node, titleAlign: PropTypes.string, view: PropTypes.string, count: PropTypes.number, columns: PropTypes.number,
    colors: PropTypes.object, button: PropTypes.node,
};

const CarouselBlock = ({ config, lang, tr }) => {
    const slides = pickLang(config?.slides, lang).filter(s => s && s.active !== false);
    const display = (config?.settings?.[lang] || config?.settings?.nl || {}).display || {};
    const first = slides[0];
    // the storefront height is up to maxHeight px; scaled down for the mockup
    const h = Math.max(110, Math.min(240, Math.round((Number(display.maxHeight) || 600) / 3)));
    return (
        <Box sx={{ mx: display.fullWidth ? -2 : 0 }}>
            <Box sx={{ position: 'relative', height: h, overflow: 'hidden' }}>
                <ImageBox url={imgUrl(first?.desktopImage)} fit={display.imageFit === 'cover' || display.imageFit === 'fill' ? 'cover' : 'contain'} label={tr('ui_landing_carousel_empty', 'Carrousel — nog geen slides')} />
                {slides.length > 1 && (
                    <Box sx={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 0.75 }}>
                        {slides.slice(0, 8).map((s, k) => (
                            <Box key={s.id || k} sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: k === 0 ? '#fff' : 'rgba(255,255,255,0.55)', boxShadow: '0 0 0 1px rgba(0,0,0,0.25)' }} />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};
CarouselBlock.propTypes = { config: PropTypes.object, lang: PropTypes.string, tr: PropTypes.func };

const IntroBlock = ({ page, lang, colors, tr }) => {
    const html = String(pickLang(page?.html, lang) || '');
    const headingMatch = html.match(/<h[1-3][^>]*>([\s\S]*?)<\/h[1-3]>/i);
    const heading = headingMatch ? stripHtml(headingMatch[1]) : '';
    const text = stripHtml(headingMatch ? html.replace(headingMatch[0], ' ') : html);
    return (
        <Box sx={{ textAlign: 'center', px: 4, py: 1 }}>
            {heading || text ? (
                <>
                    {heading && <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: colors.title, mb: 0.5 }}>{heading}</Typography>}
                    {text && (
                        <Typography sx={{ fontSize: '0.78rem', color: colors.text, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {text}
                        </Typography>
                    )}
                </>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: '40%', height: 10, backgroundColor: '#e3e3e3', borderRadius: 1 }} />
                    <Box sx={{ width: '70%', height: 7, backgroundColor: '#ececec', borderRadius: 1 }} />
                    <Box sx={{ width: '62%', height: 7, backgroundColor: '#ececec', borderRadius: 1 }} />
                    <Typography sx={{ fontSize: '0.62rem', color: '#9a9a9a', mt: 0.5 }}>{tr('ui_landing_intro_empty', 'Welkomsttekst (nog leeg of enkel in de designer)')}</Typography>
                </Box>
            )}
        </Box>
    );
};
IntroBlock.propTypes = { page: PropTypes.object, lang: PropTypes.string, colors: PropTypes.object, tr: PropTypes.func };

const ImageGridBlock = ({ config, lang, tr }) => {
    const cells = pickLang(config?.cells, lang).filter(c => c && c.active !== false);
    const layout = (config?.settings?.[lang] || config?.settings?.nl || {}).layout || {};
    const cols = Math.min(6, Math.max(1, Number(layout.columns) || 3));
    const gapPx = Math.min(16, Math.max(0, Number(layout.gap) || 0));
    const shown = cells.length ? cells.slice(0, 12) : Array.from({ length: cols }, (_, k) => ({ id: `ph${k}`, width: 1, height: 1 }));
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridAutoRows: 70, gap: `${gapPx}px` }}>
            {shown.map((c, k) => (
                <Box key={c.id || k} sx={{
                    gridColumn: `span ${Math.min(cols, Math.max(1, Number(c.width) || 1))}`,
                    gridRow: `span ${Math.min(3, Math.max(1, Number(c.height) || 1))}`,
                    overflow: 'hidden',
                }}>
                    <ImageBox url={imgUrl(c.desktopImage)} label={cells.length ? 'IMG' : tr('ui_landing_grid_empty', 'Grid — nog geen afbeeldingen')} />
                </Box>
            ))}
        </Box>
    );
};
ImageGridBlock.propTypes = { config: PropTypes.object, lang: PropTypes.string, tr: PropTypes.func };

const BrandsBlock = ({ colors, title }) => (
    <Box>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: colors.title, textAlign: 'center', mb: 1 }}>{title}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 1.25 }}>
            {['MERK', 'MERK', 'MERK', 'MERK', 'MERK', 'MERK'].map((b, k) => (
                <Box key={k} sx={{ height: 34, border: '1px solid #e2e2e2', borderRadius: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, color: '#9a9a9a', letterSpacing: 1 }}>{b}</Box>
            ))}
        </Box>
    </Box>
);
BrandsBlock.propTypes = { colors: PropTypes.object, title: PropTypes.node };

const ContainerBlock = ({ block, lang, colors, tr }) => {
    const c = block.config || {};
    const L = (lang || 'nl').toUpperCase();
    const title = c[`title${L}`] || c.titleNL || c.titleEN || c.titleFR || block.name;
    const button = c[`button${L}`] || c.buttonNL || '';
    if (containerHasBodyContent(c)) {
        const pre = (c.editor || 'unlayer') === 'unlayer' ? 'html' : 'text';
        const html = c[`${pre}${L}`] || c[`${pre}NL`] || c[`${pre}EN`] || c[`${pre}FR`] || '';
        const text = stripHtml(html);
        return (
            <Box>
                {title && <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: colors.title, textAlign: c.titlePosition || 'left', mb: 1 }}>{title}</Typography>}
                <Box sx={{ border: '1px dashed #d6d6d6', borderRadius: 1, p: 1.5, color: '#8a8a8a', fontSize: '0.7rem', textAlign: 'center' }}>
                    {text ? text.slice(0, 160) + (text.length > 160 ? '…' : '') : tr('ui_landing_container_custom', 'Eigen inhoud (designer)')}
                </Box>
            </Box>
        );
    }
    return (
        <ProductRow
            title={title}
            titleAlign={c.titlePosition || 'left'}
            view={c.viewMethod || 'horizontal'}
            columns={c.gridColumns}
            count={Number(c.maxArticles) || 4}
            colors={colors}
            button={button}
        />
    );
};
ContainerBlock.propTypes = { block: PropTypes.object, lang: PropTypes.string, colors: PropTypes.object, tr: PropTypes.func };

export const LandingMock = ({
    blocks = [], carouselConfig, imageGridConfig, introPage, lang = 'nl', colors, tr, onConfigure, onManage, labelFor,
}) => {
    // the Pagina's tab labels blocks via blockLabel (containers by their
    // reference); fall back to the i18n name when it is not passed
    const label = (b) => (labelFor ? labelFor(b) : (b.i18nKey ? tr(b.i18nKey, b.name) : b.name));
    if (!blocks.length) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.85rem', color: '#888', mb: 1.5 }}>
                    {tr('ui_landing_empty', 'Er staan nog geen blokken op de landingspagina.')}
                </Typography>
                {onManage && <Button size="small" variant="outlined" onClick={onManage}>{tr('ui_landing_manage', 'Blokken beheren')}</Button>}
            </Box>
        );
    }
    return (
        <Box sx={{ position: 'relative', px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* admin strip (not part of the page): what this is + where to change it */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mx: -2, px: 2, py: 0.5, borderBottom: '1px dashed #d9dee3', backgroundColor: 'rgba(55,71,79,0.04)' }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#6b7680' }}>
                    {tr('ui_landing_strip', "{{count}} blokken, in de volgorde van Pagina's › Landingspagina").replace('{{count}}', String(blocks.length))}
                </Typography>
                {onManage && (
                    <Button size="small" onClick={onManage} sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0, minHeight: 0 }}>
                        {tr('ui_landing_manage', 'Blokken beheren')}
                    </Button>
                )}
            </Box>
            {blocks.map((b) => {
                const id = String(b.id);
                const inactive = b.isContainer && b.config && b.config.active === false;
                let body;
                if (id === 'carousel') body = <CarouselBlock config={carouselConfig} lang={lang} tr={tr} />;
                else if (id === 'intro') body = <IntroBlock page={introPage} lang={lang} colors={colors} tr={tr} />;
                else if (id.toLowerCase() === 'imagegrid') body = <ImageGridBlock config={imageGridConfig} lang={lang} tr={tr} />;
                else if (id === 'brands') body = <BrandsBlock colors={colors} title={label(b)} />;
                else if (id.startsWith('pd_cnt-')) body = <ContainerBlock block={b} lang={lang} colors={colors} tr={tr} />;
                else body = <Placeholder label={label(b)} height={60} />;
                return (
                    <Box key={id} className="landing-mock-block" sx={{
                        position: 'relative',
                        outline: '1px dashed transparent',
                        outlineOffset: 4,
                        borderRadius: 0.5,
                        opacity: inactive ? 0.45 : 1,
                        transition: 'outline-color 120ms ease',
                        '&:hover': { outlineColor: '#b8c4d0' },
                        '&:hover .landing-mock-tag': { opacity: 1 },
                    }}>
                        {/* which block this is: shown on hover, so the page reads clean */}
                        <Box className="landing-mock-tag" sx={{
                            position: 'absolute', top: -10, left: 6, zIndex: 2, opacity: 0, transition: 'opacity 120ms ease',
                            px: 0.75, py: 0.1, borderRadius: 0.75, backgroundColor: '#37474f', color: '#fff', fontSize: '0.6rem', fontWeight: 600,
                            pointerEvents: 'none',
                        }}>
                            {label(b)}{inactive ? ` · ${tr('ui_landing_inactive', 'niet actief')}` : ''}
                        </Box>
                        {b.configurable && onConfigure && (
                            <IconButton
                                className="mock-config-icon"
                                size="small"
                                onClick={() => onConfigure(b)}
                                title={tr('cfg_tt_settings', 'Instellingen')}
                                sx={{
                                    position: 'absolute', top: 6, right: 6, zIndex: 2,
                                    width: 24, height: 24,
                                    backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                                    '& svg': { fontSize: 14 },
                                }}
                            >
                                <SettingsIcon />
                            </IconButton>
                        )}
                        {body}
                    </Box>
                );
            })}
        </Box>
    );
};
LandingMock.propTypes = {
    blocks: PropTypes.array,
    carouselConfig: PropTypes.object,
    imageGridConfig: PropTypes.object,
    introPage: PropTypes.object,
    lang: PropTypes.string,
    colors: PropTypes.object.isRequired,
    tr: PropTypes.func.isRequired,
    onConfigure: PropTypes.func,
    onManage: PropTypes.func,
    labelFor: PropTypes.func,
};

export default LandingMock;
